import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Check } from 'lucide-react';

import { api, type Paginated } from '@/lib/api';
import { Combobox } from '@/components/Combobox';
import { useWine } from './useWines';
import { fmtUsd } from '@/lib/format';

interface PriceSource {
  id: number;
  name: string;
  type: string;
}

/** Hook para listar fuentes de precio del tenant (vinaterías, "Estimado", etc.) */
function usePriceSources() {
  return useQuery({
    queryKey: ['price-sources'],
    queryFn: async () => {
      const { data } = await api.get<Paginated<PriceSource>>('/price-sources', {
        params: { per_page: 200 },
      });
      return data.data;
    },
  });
}

function useCreatePriceSource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { name: string; type: string }) => {
      const { data } = await api.post<{ data: PriceSource }>('/price-sources', payload);
      return data.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['price-sources'] }),
  });
}

function useCreatePriceObservation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      wine_id: number;
      source_id: number;
      observed_on: string;
      price: number;
      currency: string;
      notes?: string;
    }) => {
      const { data } = await api.post('/price-observations', payload);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['wine'] });
      qc.invalidateQueries({ queryKey: ['wines'] });
    },
  });
}

const today = () => new Date().toISOString().slice(0, 10);

/**
 * Página optimizada para registrar una observación de precio desde el celu.
 *
 * Flujo típico: estoy en una vinatería, encontré un vino que tengo (o que
 * busqué), abro su detalle, toco "+ Precio". Acá completo precio + fuente
 * en 3-4 taps y guardo.
 */
export function QuickPricePage() {
  const params = useParams();
  const navigate = useNavigate();
  const wineId = Number(params.id);

  const { data: wine } = useWine(wineId);
  const { data: sources } = usePriceSources();
  const create = useCreatePriceObservation();
  const createSource = useCreatePriceSource();

  const [price, setPrice] = useState('');
  const [currency, setCurrency] = useState<'ARS' | 'USD'>('ARS');
  const [sourceId, setSourceId] = useState<number | null>(null);
  const [newSourceName, setNewSourceName] = useState('');
  const [observedOn, setObservedOn] = useState(today());
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  if (!wine) {
    return <div className="p-8 text-notion-500">Cargando vino…</div>;
  }

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSavedAt(null);

    let finalSourceId = sourceId;
    try {
      // Si el usuario tipeó un nombre nuevo, lo creamos como retailer.
      if (!finalSourceId && newSourceName.trim()) {
        const created = await createSource.mutateAsync({
          name: newSourceName.trim(),
          type: 'retailer',
        });
        finalSourceId = created.id;
      }

      if (!finalSourceId) {
        setError('Elegí una vinatería o escribí una nueva.');
        return;
      }

      const parsed = Number(price.replace(',', '.'));
      if (!parsed || parsed <= 0) {
        setError('Ingresá un precio válido.');
        return;
      }

      await create.mutateAsync({
        wine_id: wineId,
        source_id: finalSourceId,
        observed_on: observedOn,
        price: parsed,
        currency,
        notes: notes.trim() || undefined,
      });

      setSavedAt(new Date().toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' }));
      setPrice('');
      setNotes('');
      setNewSourceName('');
    } catch (err: any) {
      setError(err?.response?.data?.message ?? 'No se pudo guardar.');
    }
  };

  return (
    <div className="p-4 md:p-8 max-w-xl">
      <button
        type="button"
        onClick={() => navigate(`/wines/${wineId}`)}
        className="text-sm text-notion-500 inline-flex items-center gap-1 mb-3"
      >
        <ArrowLeft size={14} /> Volver al vino
      </button>

      <header className="mb-5">
        <h1 className="text-lg font-medium leading-tight">{wine.name}</h1>
        <p className="text-xs text-notion-500">
          {wine.winery?.name} · {wine.vintage ?? '—'} · {wine.format}
        </p>
        {wine.estimated_value_usd != null && (
          <p className="text-xs text-notion-500 mt-1">
            Valor estimado actual: <strong>{fmtUsd(wine.estimated_value_usd)}</strong>
          </p>
        )}
      </header>

      <form onSubmit={submit} className="card p-4 space-y-4">
        <h2 className="font-medium text-notion-800">Nueva observación de precio</h2>

        <div className="grid grid-cols-3 gap-2">
          <div className="col-span-2">
            <label className="label">Precio</label>
            <input
              type="text"
              inputMode="decimal"
              className="input text-lg tabular-nums"
              placeholder="0"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              autoFocus
            />
          </div>
          <div>
            <label className="label">Moneda</label>
            <select
              className="input"
              value={currency}
              onChange={(e) => setCurrency(e.target.value as 'ARS' | 'USD')}
            >
              <option value="ARS">ARS</option>
              <option value="USD">USD</option>
            </select>
          </div>
        </div>

        <div>
          <label className="label">Vinatería / fuente</label>
          <Combobox
            value={sourceId}
            placeholder="Elegir existente…"
            options={(sources ?? []).map((s) => ({ id: s.id, label: s.name }))}
            onChange={(id) => setSourceId(id ? Number(id) : null)}
          />
          <p className="text-[11px] text-notion-500 mt-1">
            ¿No está en la lista? Escribila acá:
          </p>
          <input
            className="input mt-1"
            placeholder="Nueva vinatería"
            value={newSourceName}
            onChange={(e) => {
              setNewSourceName(e.target.value);
              if (e.target.value) setSourceId(null);
            }}
          />
        </div>

        <div>
          <label className="label">Fecha de la observación</label>
          <input
            type="date"
            className="input"
            value={observedOn}
            onChange={(e) => setObservedOn(e.target.value)}
            max={today()}
          />
        </div>

        <div>
          <label className="label">Notas (opcional)</label>
          <textarea
            className="input"
            rows={2}
            placeholder="Stock disponible, oferta, etc."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>

        {error && <p className="text-sm text-red-700 bg-red-50 px-3 py-2 rounded">{error}</p>}
        {savedAt && (
          <p className="text-sm text-emerald-800 bg-emerald-50 px-3 py-2 rounded inline-flex items-center gap-2">
            <Check size={14} /> Guardado a las {savedAt}. Podés cargar otra.
          </p>
        )}

        <div className="flex gap-2 pt-2">
          <button
            type="button"
            className="btn-secondary flex-1"
            onClick={() => navigate(`/wines/${wineId}`)}
          >
            Cerrar
          </button>
          <button type="submit" className="btn-primary flex-1" disabled={create.isPending}>
            {create.isPending ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </form>
    </div>
  );
}
